import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Flame, LogOut, RefreshCw, Check, Phone, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "avanti_owner_token";

const inputCls =
  "w-full bg-white border border-zinc-800 focus:border-ember focus:ring-1 focus:ring-ember outline-none px-4 py-3 text-sm text-ink placeholder:text-zinc-600 transition-colors duration-300";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="owner-login">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "circOut" }}
        className="w-full max-w-sm border border-zinc-800 bg-coal-800/60 backdrop-blur-xl p-8"
        data-testid="owner-login-form"
      >
        <div className="flex items-center gap-2 mb-2">
          <Flame className="w-5 h-5 text-ember" />
          <span className="font-display text-2xl tracking-wide">AVANTI</span>
        </div>
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 mb-8">Owner access</p>
        <label htmlFor="owner-email" className="block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Email</label>
        <input id="owner-email" data-testid="owner-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mb-5`} placeholder="you@avanti.com" />
        <label htmlFor="owner-password" className="block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Password</label>
        <input id="owner-password" data-testid="owner-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mb-6`} placeholder="••••••••" />
        {error && <p className="text-ember text-sm mb-4" data-testid="owner-login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="owner-login-submit" className="w-full bg-ember hover:bg-ember-hover disabled:opacity-50 text-white font-semibold py-3 text-sm tracking-wide uppercase transition-colors duration-300">
          {loading ? "Signing in…" : "Sign In"}
        </button>
        <a href="/" className="block mt-6 text-center font-mono text-xs tracking-[0.2em] uppercase text-zinc-600 hover:text-ember transition-colors duration-300" data-testid="owner-back-home">
          ← Back to site
        </a>
      </motion.form>
    </div>
  );
}

export default function OwnerDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [prep, setPrep] = useState(20);
  const [accepting, setAccepting] = useState(true);
  const [pauseFor, setPauseFor] = useState("until");
  const [pausedUntil, setPausedUntil] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => {
      setPrep(data.prep_minutes);
      setAccepting(data.accepting_orders);
      setPausedUntil(data.paused_until || null);
    }).catch(() => {});
  }, []);

  const toggleAccepting = async () => {
    try {
      const body = accepting
        ? { accepting_orders: false, ...(pauseFor !== "until" ? { pause_minutes: Number(pauseFor) } : {}) }
        : { accepting_orders: true };
      const { data } = await axios.patch(`${API}/settings`, body, { headers: { Authorization: `Bearer ${token}` } });
      setAccepting(data.accepting_orders);
      setPausedUntil(data.paused_until || null);
      toast.success(data.accepting_orders ? "Online ordering is ON" : "Online ordering is PAUSED", {
        description: data.accepting_orders
          ? "Customers can order again."
          : data.paused_until
          ? `Auto-resumes at ${new Date(data.paused_until).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}.`
          : "New orders are blocked until you resume.",
      });
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Couldn't update ordering status");
    }
  };

  const savePrep = async () => {
    try {
      await axios.patch(`${API}/settings`, { prep_minutes: Number(prep) }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Prep time updated", { description: `ASAP orders now show ready in ~${prep} min.` });
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error("Couldn't save prep time");
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } });
      setOrders(data);
      setError("");
    } catch (err) {
      if (err.response?.status === 401) logout();
      else setError("Couldn't load orders. Try refreshing.");
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  const markDone = async (id) => {
    try {
      const { data } = await axios.patch(
        `${API}/orders/${id}`,
        { status: "done" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
    } catch (err) {
      if (err.response?.status === 401) logout();
    }
  };

  if (!token) return <LoginForm onLogin={setToken} />;

  const todayStr = new Date().toDateString();
  const todayCount = orders.filter((o) => new Date(o.created_at).toDateString() === todayStr).length;
  const pendingCount = orders.filter((o) => o.status !== "done").length;
  const todaySales = orders
    .filter((o) => o.payment_status === "paid" && new Date(o.created_at).toDateString() === todayStr)
    .reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="min-h-screen" data-testid="owner-dashboard">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-coal-900/85 border-b border-black/10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-ember" />
            <span className="font-display text-2xl tracking-wide">AVANTI</span>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500 ml-2">Orders</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={fetchOrders} data-testid="orders-refresh-button" className="p-2 text-zinc-400 hover:text-ember transition-colors duration-300" aria-label="Refresh orders">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={logout} data-testid="owner-logout-button" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-400 hover:text-ember transition-colors duration-300">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-4 mb-10">
          <div className="border border-zinc-800 bg-coal-800/60 px-6 py-4" data-testid="orders-today-count">
            <p className="font-display text-4xl text-ember">{todayCount}</p>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Today</p>
          </div>
          <div className="border border-ember/50 bg-coal-800/60 px-6 py-4" data-testid="orders-today-sales">
            <p className="font-display text-4xl text-ember">${todaySales.toFixed(2)}</p>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Today's sales</p>
          </div>
          <div className="border border-zinc-800 bg-coal-800/60 px-6 py-4" data-testid="orders-pending-count">
            <p className="font-display text-4xl">{pendingCount}</p>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Pending</p>
          </div>
          <div className="border border-zinc-800 bg-coal-800/60 px-6 py-4" data-testid="orders-total-count">
            <p className="font-display text-4xl">{orders.length}</p>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">All time</p>
          </div>
          <div className="border border-zinc-800 bg-coal-800/60 px-6 py-4 flex items-end gap-3" data-testid="prep-time-setting">
            <div>
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-2">Prep time (min)</p>
              <input
                type="number"
                min={5}
                max={120}
                value={prep}
                onChange={(e) => setPrep(Math.max(5, Math.min(120, Number(e.target.value) || 5)))}
                className="w-24 bg-white border border-zinc-800 focus:border-ember outline-none px-3 py-2 text-sm text-ink"
                data-testid="prep-time-input"
              />
            </div>
            <button
              onClick={savePrep}
              data-testid="prep-time-save"
              className="bg-ember hover:bg-ember-hover text-white font-semibold text-xs tracking-wide uppercase px-4 py-2 transition-colors duration-300"
            >
              Save
            </button>
          </div>
          <div className="border border-zinc-800 bg-coal-800/60 px-6 py-4 flex items-end gap-4" data-testid="ordering-toggle-setting">
            <div>
              <p className="font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 mb-1">Online orders</p>
              <p className={`font-display text-3xl ${accepting ? "text-ember" : "text-zinc-500"}`} data-testid="ordering-status">
                {accepting ? "ON" : "PAUSED"}
              </p>
              {!accepting && (
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-500" data-testid="ordering-resume-at">
                  {pausedUntil
                    ? `Resumes ${new Date(pausedUntil).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}`
                    : "Until you resume"}
                </p>
              )}
            </div>
            {accepting && (
              <select
                aria-label="Pause duration"
                data-testid="pause-duration-select"
                value={pauseFor}
                onChange={(e) => setPauseFor(e.target.value)}
                className="bg-white border border-zinc-800 focus:border-ember outline-none px-3 py-2 text-sm text-ink"
              >
                <option value="until">Until I resume</option>
                <option value="30">For 30 min</option>
                <option value="60">For 60 min</option>
              </select>
            )}
            <button
              onClick={toggleAccepting}
              data-testid="ordering-toggle"
              className={`font-semibold text-xs tracking-wide uppercase px-4 py-2 transition-colors duration-300 ${
                accepting ? "border border-zinc-800 text-zinc-500 hover:border-ember hover:text-ember" : "bg-ember hover:bg-ember-hover text-white"
              }`}
            >
              {accepting ? "Pause" : "Resume now"}
            </button>
          </div>
        </div>

        {error && <p className="text-ember text-sm mb-6" data-testid="orders-error">{error}</p>}

        {orders.length === 0 && !loading && (
          <p className="text-zinc-500 font-mono text-sm" data-testid="orders-empty">No orders yet — new ones appear here automatically.</p>
        )}

        <div className="space-y-4" data-testid="orders-list">
          {orders.map((o) => (
            <motion.div
              key={o.id}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className={`border p-6 transition-colors duration-300 ${o.status === "done" ? "border-zinc-800 opacity-50" : "border-ember/40 bg-coal-800/60"}`}
              data-testid={`order-card-${o.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="font-display text-2xl tracking-wide">{o.name}</h3>
                    <span className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${o.status === "done" ? "border-zinc-700 text-zinc-500" : "border-ember text-ember"}`} data-testid={`order-status-${o.id}`}>
                      {o.status === "done" ? "Done" : o.status === "pending_payment" ? "Awaiting payment" : "New"}
                    </span>
                    <span className={`font-mono text-[10px] tracking-[0.2em] uppercase px-2 py-1 border ${o.payment_status === "paid" ? "border-ember bg-ember text-white" : "border-zinc-700 text-zinc-500"}`} data-testid={`order-payment-${o.id}`}>
                      {o.payment_status === "paid" ? `Paid $${(o.total || 0).toFixed(2)}` : o.total ? `Unpaid $${o.total.toFixed(2)}` : "No payment"}
                    </span>
                  </div>
                  <a href={`tel:${o.phone}`} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-ember transition-colors duration-300" data-testid={`order-phone-${o.id}`}>
                    <Phone className="w-3.5 h-3.5" /> {o.phone}
                  </a>
                  <p className="mt-1 font-mono text-xs tracking-[0.15em] uppercase text-ember" data-testid={`order-pickup-${o.id}`}>
                    Pickup: {o.pickup_time || "ASAP"}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-3">
                  <span className="inline-flex items-center gap-2 font-mono text-xs text-zinc-500" data-testid={`order-time-${o.id}`}>
                    <Clock className="w-3.5 h-3.5" />
                    {new Date(o.created_at).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                  </span>
                  {o.status !== "done" && (
                    <button onClick={() => markDone(o.id)} data-testid={`order-done-${o.id}`} className="inline-flex items-center gap-2 bg-ember hover:bg-ember-hover text-white font-semibold text-xs tracking-wide uppercase px-4 py-2 transition-colors duration-300">
                      <Check className="w-4 h-4" /> Mark Done
                    </button>
                  )}
                </div>
              </div>
              <p className="mt-4 text-ink whitespace-pre-line" data-testid={`order-items-${o.id}`}>{o.items}</p>
              {o.notes && <p className="mt-2 text-sm text-zinc-500" data-testid={`order-notes-${o.id}`}>Notes: {o.notes}</p>}
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
